// Components
import { Image, useMaxWidth, Pill } from '@tracklib/kit'
import { Hero as KitHero } from 'kit'

// Types
import { CmsPage } from 'types'

// Utils
import { getYoutubeIdFromUrl } from '@constants'
import { getDocumentUrl } from 'utils'

import styles from './hero.css'

export const youtubeRegex =
  /(?:youtube(?:-nocookie)?\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

type HeroProps = {
  article: CmsPage
}

const Hero = ({ article }: HeroProps): JSX.Element | null => {
  const isSmall = useMaxWidth()

  if (!article) {
    return null
  }

  const {
    video,
    image_only_hero: imageOnlyHero,
    categories,
    title,
    image,
    mobile_override_image,
    meta,
    video_only_hero_mp4,
    video_only_hero_webm,
    video_only_hero_mobile_mp4,
    video_only_hero_mobile_webm,
  } = article

  const imageId = image && image.id
  const mobileImageId = mobile_override_image && mobile_override_image.id

  const hasVideo = video && video?.match(youtubeRegex)?.[1]

  if (video_only_hero_mp4 && video_only_hero_webm) {
    return (
      <KitHero
        image={imageId}
        mobileImageOverride={mobileImageId}
        video={getDocumentUrl(video_only_hero_webm?.id)}
        videoMp4={getDocumentUrl(video_only_hero_mp4?.id)}
        mobileVideoOverride={getDocumentUrl(video_only_hero_mobile_webm?.id)}
        mobileVideoOverrideMp4={getDocumentUrl(video_only_hero_mobile_mp4?.id)}
        marginBottom={30}
        marginBottomMobile={20}
        large
      />
    )
  }

  if (hasVideo) {
    return (
      <div className={styles.video}>
        <Image
          image={isSmall && mobileImageId ? mobileImageId : imageId}
          alt={title}
          title={title}
          sizes="100vw"
          gradient
          absolute
          className={styles.blurredImage}
        />
        <div className={styles.videoWrapper}>
          <div className="iframeEmbed">
            <iframe
              width="500"
              height="300"
              src={`https://www.youtube.com/embed/${getYoutubeIdFromUrl(video)}`}
              frameBorder="0"
              title="article-video"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    )
  }

  if (imageOnlyHero) {
    if (meta.slug === 'tracklib-presents-state-of-sampling-2019') {
      return (
        <h1 className={styles.stateOfSamplingImage}>
          <Image image={isSmall && mobileImageId ? mobileImageId : imageId} alt={title} title={title} sizes="100vw" absolute />
        </h1>
      )
    }
    return (
      <h1 className={styles.image}>
        <Image image={isSmall && mobileImageId ? mobileImageId : imageId} alt={title} title={title} sizes="100vw" />
      </h1>
    )
  }
  return (
    <header className={styles.default}>
      <Image image={isSmall && mobileImageId ? mobileImageId : imageId} alt={title} title={title} sizes="100vw" gradient absolute />
      <div className={styles.container}>
        <div>
          <div className={styles.pill}>
            {categories.map(x => (
              <Pill type="opaque" key={x.id} className={styles.category}>
                {x.name}
              </Pill>
            ))}
          </div>
          <h1 className={styles.title}>{title}</h1>
        </div>
      </div>
    </header>
  )
}

export default Hero
